import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiShield,
  FiAlertCircle,
  FiGlobe,
  FiChevronDown,
  FiArrowRight,
} from "react-icons/fi";

import { loginApi } from "../api/endpoints";
import "../assets/css/king999-login-final.scss";

function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ email: "", password: "" });

  useEffect(() => {
    document.body.classList.add("dt-login-active");
    return () => document.body.classList.remove("dt-login-active");
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const email = formData.email.trim();
    const password = formData.password;

    if (!email && !password) {
      setError("Please enter your email/username and password.");
      return;
    }
    if (!email) {
      setError("Please enter your email or username.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const apiResponse = await loginApi({ email, password });
      const response =
        apiResponse?.data?.success !== undefined ? apiResponse.data : apiResponse;

      if (!response?.success) {
        setError(response?.message || "Invalid email or password.");
        return;
      }

      const { admin, accessToken, refreshToken } = response.data || {};
      if (!accessToken) {
        setError("Login successful, but authentication token was not received.");
        return;
      }

      localStorage.setItem("token", accessToken);
      localStorage.setItem("accessToken", accessToken);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
      if (admin) localStorage.setItem("admin", JSON.stringify(admin));

      if (remember) localStorage.setItem("rememberAdmin", "1");
      else localStorage.removeItem("rememberAdmin");

      sessionStorage.setItem("justLoggedIn", "1");
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      let message =
        err?.response?.data?.message || err?.response?.data?.error || "";

      if (!message) {
        if (status === 400) message = "Please enter valid login details.";
        else if (status === 401) message = "Invalid email or password.";
        else if (status === 403)
          message = "Your admin account is disabled. Please contact the administrator.";
        else if (status === 404) message = "Login service was not found.";
        else if (status >= 500) message = "Server error. Please try again after some time.";
        else if (!navigator.onLine)
          message = "No internet connection. Please check your network.";
        else message = "Unable to sign in. Please check your credentials.";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="king-login-page">
      <section className="king-login-visual" aria-label="RAM RUMMY Admin Panel" />

      <section className="king-login-side">
        <div className="king-login-card">
          <div className="king-login-language" aria-label="Language">
            <FiGlobe />
            <span>English</span>
            <FiChevronDown />
          </div>

          <div className="king-login-heading">
            <h1>Welcome Back</h1>
            <p>
              Login to your RAM RUMMY admin panel
              <br className="king-login-desktop-break" /> to continue managing your business.
            </p>
          </div>

          {error && (
            <div className="king-login-error" role="alert">
              <FiAlertCircle />
              <span>{error}</span>
            </div>
          )}

          <form className="king-login-form" onSubmit={handleLogin} noValidate>
            <div className="king-login-group">
              <label htmlFor="email">Email or Username</label>
              <div className="king-login-input">
                <FiMail />
                <input
                  id="email"
                  name="email"
                  type="text"
                  placeholder="Enter your email or username"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            <div className="king-login-group">
              <label htmlFor="password">Password</label>
              <div className="king-login-input">
                <FiLock />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="king-login-eye"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="king-login-options king-login-options--single">
              <label className="king-login-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button className="king-login-submit" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  <span>Signing in…</span>
                </>
              ) : (
                <>
                  <span>Login</span>
                  <FiArrowRight />
                </>
              )}
            </button>
          </form>

          <div className="king-login-security">
            <span className="king-login-security-icon">
              <FiShield />
            </span>
            <p>
              Your data is protected with industry-standard
              <br className="king-login-desktop-break" /> security and encryption.
            </p>
          </div>
        </div>

        <footer className="king-login-footer">
          <span>© 2026 RAM RUMMY. All rights reserved.</span>
          <span><FiShield /> Secure &nbsp;•&nbsp; Reliable &nbsp;•&nbsp; Trusted Gaming Platform</span>
        </footer>
      </section>
    </main>
  );
}

export default Login;
